/**
 * Die Bedenkzeit-Arithmetik (§27, §38) — nur in Variante B.
 *
 * **Warum das in der Engine liegt.** Entschieden am 2026-10-03: `apps/sim` soll ab V3 auch
 * Zeitpartien headless durchspielen können, damit sich fragen lässt, ob das Zeitformat ein
 * anderes Spiel ergibt. Server und Simulator müssen dann **identisch** rechnen — zwei
 * Implementierungen würden irgendwann verschieden urteilen, und bei „wer hat gewonnen?" wäre
 * das teuer. Die Rechnung ist pur, also gehört sie hierher.
 *
 * **Was hier ausdrücklich nicht passiert.** Die Uhr wird nicht *getrieben*: kein
 * `setInterval`, kein `Date.now()`, kein Aufwachen bei Zeitablauf, kein Persistieren. `now`
 * kommt als Parameter herein (`CLAUDE.md` Regel 4). Das Treiben ist Server-Sache
 * (`ARCHITECTURE.md` E8) — und in der Simulation gibt es gar keine echte Zeit, sondern nur
 * fortgeschriebene Zeitstempel. Genau deshalb trägt diese Datei kein Wissen darüber, woher
 * `now` kommt.
 *
 * **Das Modell: ein Guthaben plus ein Startzeitpunkt.** Gespeichert wird je Spieler die
 * verbuchte Restzeit, dazu höchstens **eine** laufende Uhr mit dem Zeitpunkt ihres Starts
 * (§38 — nur die Uhr des aktiven Spielers läuft). Die aktuelle Restzeit ist daraus jederzeit
 * ausrechenbar, statt von einem Intervall mitgeführt zu werden. Das ist der Grund, warum ein
 * Serverneustart keine Zeit verschenkt und keine erfindet: es gibt nichts zu rekonstruieren,
 * nur etwas zu rechnen.
 *
 * **Alles in Millisekunden.** Der Host stellt Minuten ein (§1081); umgerechnet wird dort, wo
 * die Partie entsteht. Eine Einheit im Rechenkern, keine zwei — eine halb umgerechnete Zahl
 * wäre ein Fehler, der sich als „die Uhr läuft sechzigmal zu schnell" zeigt.
 *
 * **Kein Zuschlag pro Zug.** §27 und §1081 legen ein einziges Budget fest. Ein Inkrement
 * wäre eine neue Regel und keine Umsetzung der bestehenden (`GAME_RULES.md`, Abschnitt 9).
 */
function requireKnown(timer, player) {
    const booked = timer.remaining.get(player);
    if (booked === undefined) {
        throw new Error(`Unbekannter Spieler in der Bedenkzeit: '${player}'`);
    }
    return booked;
}
/**
 * Die seit dem Start verstrichene Zeit — **nie negativ**.
 *
 * Die untere Schranke ist kein Schönheitsfehler-Fix: NTP-Korrekturen und Zeitumstellungen
 * können `now` kleiner machen als den Startpunkt. Ohne sie würde ein Spieler dabei Zeit
 * **gewinnen**, und zwar unauffällig.
 */
function elapsed(since, now) {
    return Math.max(0, now - since);
}
/**
 * Legt die Uhr an.
 *
 * **Eine leere Spielerliste ist erlaubt** (Nr. 31): in der Lobby darf vorübergehend niemand
 * einer Farbe zugeordnet sein, und das Guthaben ist trotzdem schon eingestellt. Was dann
 * entsteht, ist eine Uhr ohne Restzeiten — und genau nicht eine Partie ohne Bedenkzeit, die
 * Nr. 20 verbietet.
 */
export function createTimerState(players, budgetMs) {
    if (!Number.isInteger(budgetMs) || budgetMs <= 0) {
        // Ein Budget von 0 hieße: der erste Spieler verliert, bevor er zieht. Das ist ein
        // Konfigurationsfehler und keine Partie.
        throw new Error(`Die Bedenkzeit muss eine positive ganze Zahl in Millisekunden sein, nicht ${budgetMs}`);
    }
    const remaining = new Map();
    for (const player of players) {
        if (remaining.has(player)) {
            throw new Error(`Spieler '${player}' kommt in der Bedenkzeit mehrfach vor`);
        }
        remaining.set(player, budgetMs);
    }
    return { budgetMs, remaining, running: undefined };
}
/**
 * Die Restzeit dieses Spielers zum Zeitpunkt `now`.
 *
 * Für jeden, dessen Uhr nicht läuft, ist das der verbuchte Stand — unabhängig von `now`.
 */
export function remainingAt(timer, player, now) {
    const booked = requireKnown(timer, player);
    if (timer.running?.player !== player) {
        return booked;
    }
    return Math.max(0, booked - elapsed(timer.running.since, now));
}
export function isClockRunning(timer) {
    return timer.running !== undefined;
}
/**
 * Startet die Uhr dieses Spielers. Verbucht nichts — merkt sich nur den Zeitpunkt.
 *
 * Läuft schon eine Uhr, ist das ein Fehler im Aufrufpfad und wird abgewiesen: nach §38 läuft
 * genau eine. Beim Zugwechsel also `startClock(stopClock(timer, now), next, now)` — der Stopp
 * verbucht, der Start setzt neu an, und dass beides denselben Zeitpunkt benutzt, macht die
 * Übergabe lückenlos.
 */
export function startClock(timer, player, now) {
    requireKnown(timer, player);
    if (timer.running !== undefined) {
        throw new Error(`Die Uhr von '${timer.running.player}' läuft noch — es läuft immer nur eine (§38)`);
    }
    return { budgetMs: timer.budgetMs, remaining: timer.remaining, running: { player, since: now } };
}
/**
 * Hält die laufende Uhr an und **verbucht** die verstrichene Zeit.
 *
 * Gebraucht an drei Stellen: nach erfolgreichem Commit (§38 — und nur dort, damit die Preview
 * die Uhr nicht anhalten kann), beim Pausieren durch den Admin (`OPEN_QUESTIONS.md` Nr. 26)
 * und am Partieende.
 *
 * Ohne laufende Uhr wirkungslos — damit ist der Aufruf gefahrlos wiederholbar, und der Server
 * muss vor dem Stoppen nicht prüfen, ob überhaupt etwas läuft.
 */
export function stopClock(timer, now) {
    const running = timer.running;
    if (running === undefined) {
        return timer;
    }
    const remaining = new Map(timer.remaining);
    remaining.set(running.player, remainingAt(timer, running.player, now));
    return { budgetMs: timer.budgetMs, remaining, running: undefined };
}
/**
 * Wessen Zeit zum Zeitpunkt `now` abgelaufen ist — oder `undefined`.
 *
 * Geprüft wird ausschließlich die **laufende** Uhr: nur sie kann neu ablaufen. Ein Spieler
 * mit verbuchten 0 ms, dessen Uhr steht, ist entweder schon ausgeschieden (`applyTimeout`)
 * oder gerade pausiert — ihn hier zu nennen würde den Timeout ein zweites Mal auslösen.
 */
export function expiredPlayer(timer, now) {
    const running = timer.running;
    if (running === undefined) {
        return undefined;
    }
    return remainingAt(timer, running.player, now) === 0 ? running.player : undefined;
}
//# sourceMappingURL=timer.js.map